import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError, downloadAuthorizedDocument } from "@/lib/api";
import { Download, X, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

function prettyFilename(name) {
  if (!name) return "";
  let base = name.split(/[\\/]/).pop();
  base = base.replace(/^\d{12,17}_/, "");
  base = base.replace(/[_-]watermarked(?=\.[^.]+$)/i, "");
  return base;
}

/**
 * Inline document previewer modal.
 *
 * Fetches the file with the user's auth token, then renders it inside the
 * modal so the user can scroll through it without downloading:
 *   - PDFs → <iframe> (native pagination + scroll + zoom)
 *   - Images → <img> centered
 *   - Text / CSV / JSON → <pre> text
 *   - Anything else → "Preview not available — download instead."
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   path       — api-relative download path, e.g. `/documents/<id>/download`
 *   filename   — display name
 *   mimeType   — optional hint (server also sets Content-Type on the blob)
 */
export default function DocumentPreviewModal({ open, onClose, path, filename, mimeType }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState(mimeType || "");
  const [textContent, setTextContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const controllerRef = useRef(null);

  useEffect(() => {
    if (!open || !path) return;
    setLoading(true);
    setError(null);
    setProgress(0);
    // Revoke any previous blob URL before we replace it
    setBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setTextContent(null);
    const controller = new AbortController();
    controllerRef.current = controller;
    api
      .get(path, {
        responseType: "blob",
        signal: controller.signal,
        timeout: 60_000,
        onDownloadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          else if (e.loaded) setProgress(Math.min(90, Math.round(e.loaded / (1024 * 30))));
        },
      })
      .then(async (res) => {
        const ct = res.headers["content-type"] || mimeType || "application/octet-stream";
        setContentType(ct);
        // For text-ish content, decode so we can show it in a scrollable <pre>
        if (ct.startsWith("text/") || ct.includes("json") || ct.includes("csv")) {
          const txt = await res.data.text();
          setTextContent(txt);
        } else {
          const url = URL.createObjectURL(res.data);
          setBlobUrl(url);
        }
      })
      .catch(async (err) => {
        if (err.name === "CanceledError" || err.name === "AbortError") return;
        // Server responded 4xx/5xx with a JSON body — but axios delivers the
        // body as a Blob because we asked for responseType:'blob'. Decode it.
        try {
          const data = err?.response?.data;
          if (data instanceof Blob) {
            const text = await data.text();
            try {
              const parsed = JSON.parse(text);
              setError(formatApiError({ response: { data: parsed } }));
              return;
            } catch {
              if (text) { setError(text.slice(0, 300)); return; }
            }
          }
        } catch { /* ignore */ }
        setError(formatApiError(err) || "Could not load document");
      })
      .finally(() => setLoading(false));
    return () => {
      controller.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, path]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isPdf = contentType.includes("pdf");
  const isImage = contentType.startsWith("image/");

  const doDownload = () =>
    downloadAuthorizedDocument(path, filename).catch((e) => toast.error(e.message || "Download failed"));

  const openInNewTab = () => {
    if (!blobUrl) return;
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-stretch p-4 sm:p-8"
      onClick={onClose}
      data-testid="doc-preview-modal"
    >
      <div
        className="mx-auto w-full max-w-6xl bg-white border border-slate-300 flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-200">
          <div className="min-w-0">
            <div className="small-caps text-slate-500">Document preview</div>
            <div className="mt-1 font-semibold text-[16px] text-[#1F2937] truncate" title={filename}>
              {prettyFilename(filename)}
            </div>
          </div>
          <div className="flex items-center gap-5 shrink-0">
            {blobUrl && (
              <button
                onClick={openInNewTab}
                className="small-caps text-[#1F2937] hover:opacity-70 inline-flex items-center gap-2"
                data-testid="doc-preview-newtab"
                title="Open in new tab"
              >
                <ExternalLink className="h-3.5 w-3.5" /> New tab
              </button>
            )}
            <button
              onClick={doDownload}
              className="small-caps text-[#1F2937] hover:opacity-70 inline-flex items-center gap-2"
              data-testid="doc-preview-download"
              title="Download"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-[#1F2937] p-1"
              data-testid="doc-preview-close"
              title="Close (Esc)"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="flex-1 min-h-0 bg-slate-100 relative">
          {loading && (
            <div className="absolute inset-0 grid place-items-center text-slate-500 gap-3 p-6">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="small-caps" data-testid="doc-preview-loading">
                {progress > 0 ? `Loading ${progress}%…` : "Loading…"}
              </span>
              <button
                onClick={doDownload}
                className="mt-4 small-caps text-[#1F2937] underline underline-offset-4 hover:opacity-70"
                data-testid="doc-preview-slow-download"
                title="Taking too long? Download instead"
              >
                <Download className="h-3.5 w-3.5 inline mr-1" /> Taking too long? Download instead
              </button>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 grid place-items-center p-8 text-center">
              <div>
                <div className="small-caps text-red-700">Could not load</div>
                <p className="mt-2 text-slate-700 text-[15px] max-w-md">{error}</p>
                <button
                  onClick={doDownload}
                  className="mt-6 small-caps text-[#1F2937] inline-flex items-center gap-2 underline underline-offset-4"
                >
                  <Download className="h-3.5 w-3.5" /> Try downloading instead
                </button>
              </div>
            </div>
          )}
          {!loading && !error && isPdf && blobUrl && (
            <iframe
              src={blobUrl}
              title={filename}
              className="w-full h-full min-h-[60vh] bg-white"
              data-testid="doc-preview-pdf"
            />
          )}
          {!loading && !error && isImage && blobUrl && (
            <div className="w-full h-full overflow-auto p-6">
              <img
                src={blobUrl}
                alt={filename}
                className="mx-auto max-w-full h-auto"
                data-testid="doc-preview-image"
              />
            </div>
          )}
          {!loading && !error && textContent != null && (
            <pre
              className="w-full h-full overflow-auto p-6 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-800 bg-white"
              data-testid="doc-preview-text"
            >
              {textContent}
            </pre>
          )}
          {!loading && !error && !isPdf && !isImage && textContent == null && blobUrl && (
            <div className="absolute inset-0 grid place-items-center p-8 text-center">
              <div>
                <div className="small-caps text-slate-500">Inline preview not available</div>
                <p className="mt-2 text-slate-600 text-[15px] max-w-md">
                  This file type ({contentType || "unknown"}) can&apos;t be previewed in the browser.
                </p>
                <button
                  onClick={doDownload}
                  className="mt-6 small-caps text-[#1F2937] inline-flex items-center gap-2 underline underline-offset-4"
                  data-testid="doc-preview-fallback-download"
                >
                  <Download className="h-3.5 w-3.5" /> Download to view
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
